// Place Value Tower: digit blocks fall one at a time and wait; she taps the column where
// each belongs. Zeros matter. At the top rungs, decimals and a question after the build.

import { h, clear } from '@kit/dom';
import { makeSurface, roundRect } from '@kit/canvas';
import { attachPointer } from '@kit/pointer';
import { makeLoop } from '@kit/loop';
import type { GameModule, GameContext } from '@shell/types';
import { makePlaceValue, validColumns, PLACE_LABEL, type PVProblem } from '@problems/place-value';
import { makeNumpad, makeChoices } from '@games/common/numpad';

const BUILDS = 6;
const PLACE_COLOR: Record<string, string> = { thousands: '#9b6be6', hundreds: '#4f8fe6', tens: '#3bb3a9', ones: '#f08a3c', tenths: '#ee86b7', hundredths: '#5fbf6a' };

function mount(host: HTMLElement, ctx: GameContext) {
  let n = 0;
  let level = ctx.currentLevel();
  let p: PVProblem = makePlaceValue(level, ctx.rng);
  let filled: boolean[] = [];
  let orderIdx = 0;               // which falling block we are on
  let blockY = 0, blockTargetY = 0, blockX = 0, blockTargetX = 0;
  let landing: { col: number; until: number } | null = null;
  let flashCol: { col: number; until: number } | null = null;
  let firstTry = true;
  let attempts = 0, correct = 0, minLevel = level;
  let phase: 'build' | 'question' | 'done' = 'build';
  const t0 = performance.now();
  let tProblem = performance.now();

  const head = h('div.row', { style: { justifyContent: 'space-between' } }, h('span.muted', null, 'Tower ', h('strong', null, '1'), ` of ${BUILDS}`), h('span.muted.small', null, 'Tap the right column'));
  const target = h('div.center', { style: { fontWeight: '900', fontSize: '30px', minHeight: '40px', letterSpacing: '2px' } });
  const stage = h('div');
  const qArea = h('div');
  host.append(head, target, stage, qArea);

  const S = makeSurface(stage, { aspect: 0.8, draw: false, onResize: () => { placeBlock(true); } });
  const loop = makeLoop(() => {
    blockY += (blockTargetY - blockY) * 0.1;
    blockX += (blockTargetX - blockX) * 0.15;
  }, draw);
  loop.start();

  function cols(): number { return p.columns.length; }
  function colX(i: number): number { return (i + 0.5) * (S.w / cols()); }
  function blockSize(): number { return Math.min(S.w / cols() * 0.7, S.h * 0.11); }

  function setup() {
    level = ctx.currentLevel();
    minLevel = Math.min(minLevel, level);
    p = makePlaceValue(level, ctx.rng);
    filled = p.columns.map(() => false);
    orderIdx = 0; phase = 'build'; firstTry = true;
    clear(qArea);
    (head.querySelector('strong') as HTMLElement).textContent = String(n + 1);
    (head.lastElementChild as HTMLElement).textContent = 'Tap the right column';
    renderTarget();
    tProblem = performance.now();
    placeBlock(true);
  }

  function renderTarget() {
    clear(target);
    target.append(h('span.muted', { style: { fontSize: '18px', fontWeight: '700', marginRight: '8px' } }, p.kind === 'build' ? 'Build' : 'Build, then answer:'));
    // digits with separators, lit when placed
    const whole = p.columns.filter(c => !c.place.endsWith('ths'));
    const frac = p.columns.filter(c => c.place.endsWith('ths'));
    const span = (c: typeof p.columns[number], i: number) => h('span', { style: { color: filled[i] ? PLACE_COLOR[c.place] : 'var(--line)' } }, String(c.digit));
    whole.forEach((c, k) => {
      const i = p.columns.indexOf(c);
      if (k > 0 && (whole.length - k) % 3 === 0) target.append(h('span.muted', null, ','));
      target.append(span(c, i));
    });
    if (frac.length) { target.append(h('span', null, '.')); frac.forEach(c => target.append(span(c, p.columns.indexOf(c)))); }
  }

  function currentDigit(): number | null {
    if (orderIdx >= p.order.length) return null;
    return p.columns[p.order[orderIdx]].digit;
  }

  function placeBlock(snap: boolean) {
    blockTargetX = S.w / 2; blockTargetY = S.h * 0.14;
    if (snap) { blockX = blockTargetX; blockY = -blockSize(); }
  }

  const unPointer = attachPointer(S.canvas, {
    tap(pt) {
      if (phase !== 'build' || landing) return;
      const d = currentDigit(); if (d === null) return;
      const col = Math.floor(pt.x / (S.w / cols()));
      if (col < 0 || col >= cols()) return;
      const valid = validColumns(p, d, filled);
      const ms = performance.now() - tProblem;
      if (valid.includes(col)) {
        if (firstTry) { attempts++; correct++; ctx.report({ skill: 'place_value', level, correct: true, hintUsed: false, ms, problemId: `pv:${p.display}` }); }
        else { ctx.report({ skill: 'place_value', level, correct: true, hintUsed: true, ms, problemId: `pv:${p.display}` }); }
        ctx.sound('ding');
        filled[col] = true;
        landing = { col, until: performance.now() + 350 };
        blockTargetX = colX(col); blockTargetY = stackY(col);
        renderTarget();
        setTimeout(() => {
          landing = null; orderIdx++; firstTry = true; tProblem = performance.now();
          if (orderIdx >= p.order.length) finishBuild(); else placeBlock(true);
        }, 380);
      } else {
        if (firstTry) { attempts++; ctx.report({ skill: 'place_value', level, correct: false, hintUsed: false, ms, problemId: `pv:${p.display}` }); firstTry = false; }
        ctx.sound('buzz');
        flashCol = { col, until: performance.now() + 450 };
        const want = PLACE_LABEL[p.columns[valid[0]].place];
        ctx.say(`${d} belongs in the ${want} place.`);
      }
    },
  });

  function stackY(col: number): number {
    // blocks stack from the floor; one block per column in this game
    void col;
    return S.h * 0.78 - blockSize();
  }

  function finishBuild() {
    if (p.kind === 'build') {
      ctx.say(`${p.display}. Built!`);
      phase = 'done';
      next();
      return;
    }
    phase = 'question';
    (head.lastElementChild as HTMLElement).textContent = p.kind === 'round' ? 'Round it' : 'Add it up';
    ctx.say(p.text);
    clear(qArea);
    const tq = performance.now();
    let answered = false;
    const answer = (v: number) => {
      if (answered) return;
      const ok = Math.abs(v - (p.answer ?? NaN)) < 0.001;
      attempts++;
      if (ok) { answered = true; correct++; ctx.sound('ding'); ctx.say(`Yes, ${p.answer}.`); ctx.report({ skill: 'place_value', level, correct: true, hintUsed: false, ms: performance.now() - tq, problemId: `pvq:${p.display}` }); phase = 'done'; next(); }
      else { ctx.sound('buzz'); ctx.report({ skill: 'place_value', level, correct: false, hintUsed: false, ms: performance.now() - tq, problemId: `pvq:${p.display}` }); ctx.say(p.kind === 'round' ? `Look at the digit to the right of the place you are rounding to. 5 or more rounds up.` : `Add each part: the answer is the number you just built.`); }
    };
    qArea.append(h('p.center', { style: { fontWeight: '800' } }, p.text));
    if (p.choices) qArea.append(makeChoices(p.choices as number[], (v) => answer(Number(v))).el);
    else qArea.append(makeNumpad({ onEnter: answer, allowDecimal: p.decimals > 0 }).el);
  }

  function next() {
    n++;
    setTimeout(() => {
      if (n >= BUILDS) {
        if (attempts === correct && minLevel >= 5) ctx.charm('charm.tower');
        ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0 });
      } else setup();
    }, 1000);
  }

  function draw() {
    const c = S.ctx, W = S.w, H = S.h;
    c.fillStyle = '#efe9db'; c.fillRect(0, 0, W, H);
    const k = cols(), cw = W / k, bs = blockSize();
    const now = performance.now();
    if (flashCol && now > flashCol.until) flashCol = null;
    // columns
    for (let i = 0; i < k; i++) {
      const col = p.columns[i];
      c.fillStyle = flashCol?.col === i ? 'rgba(226,87,76,.25)' : i % 2 ? 'rgba(0,0,0,.04)' : 'rgba(0,0,0,.02)';
      c.fillRect(i * cw, 0, cw, H * 0.8);
      // floor label
      c.fillStyle = PLACE_COLOR[col.place]; roundRect(c, i * cw + 4, H * 0.8, cw - 8, H * 0.16, 6); c.fill();
      c.fillStyle = '#fff'; c.font = `800 ${Math.round(Math.min(cw * 0.22, 15))}px Nunito, sans-serif`; c.textAlign = 'center'; c.textBaseline = 'middle';
      c.fillText(PLACE_LABEL[col.place], colX(i), H * 0.88);
      // placed block
      if (filled[i] && !(landing && landing.col === i)) {
        drawBlock(c, colX(i), stackY(i), bs, col.digit, PLACE_COLOR[col.place]);
      }
    }
    // decimal point marker between ones and tenths
    const tenthsIdx = p.columns.findIndex(cc => cc.place === 'tenths');
    if (tenthsIdx > 0) { c.fillStyle = '#2a2a2e'; c.beginPath(); c.arc(tenthsIdx * cw, H * 0.78 - 6, 5, 0, 7); c.fill(); }
    // falling block
    const d = currentDigit();
    if (d !== null && phase === 'build') {
      const col = landing ? p.columns[landing.col] : null;
      drawBlock(c, blockX, blockY, bs, d, col ? PLACE_COLOR[col.place] : '#9a9aa3');
      if (!landing) { c.fillStyle = '#6a6a72'; c.font = `700 ${Math.round(bs * 0.3)}px Nunito, sans-serif`; c.textAlign = 'center'; c.textBaseline = 'alphabetic'; c.fillText('Where does this go?', W / 2, blockY + bs + bs * 0.45); }
    }
  }

  function drawBlock(c: CanvasRenderingContext2D, cx: number, y: number, s: number, digit: number, color: string) {
    c.fillStyle = color; roundRect(c, cx - s / 2, y, s, s, 8); c.fill();
    c.fillStyle = 'rgba(255,255,255,.25)'; c.fillRect(cx - s / 2 + 4, y + 4, s - 8, s * 0.18);
    c.fillStyle = '#fff'; c.font = `900 ${Math.round(s * 0.55)}px Nunito, sans-serif`; c.textAlign = 'center'; c.textBaseline = 'middle';
    c.fillText(String(digit), cx, y + s / 2 + 1);
  }

  setup();
  return { destroy() { loop.stop(); unPointer(); S.destroy(); clear(host); } };
}

const mod: GameModule = {
  id: 'math.place-value',
  title: 'Place Value Tower',
  blurb: 'Digit blocks fall one at a time. Stack each in its place to build the number.',
  category: 'math',
  skills: ['place_value'],
  levelRange: [1, 10],
  unlock: { stars: 8 },
  mount,
};
export default mod;
